// apps/frontend/src/components/common/UpsellModal.tsx
// Modale d'upsell affichée quand un endpoint backend renvoie 402
// (Payment Required) avec un upgradePlan suggéré. Utilisé via le hook
// useUpsell() depuis n'importe où dans l'app.
//
// Format de la réponse 402 attendue :
//   { success: false, code: 'AI_QUOTA_EXCEEDED' | 'BG_REMOVAL_PREMIUM' | ...,
//     message: string, upgradePlan: 'BASIC' | 'PRO' }

import React from 'react';
import { X, Sparkles, Crown, ArrowRight, Lock } from 'lucide-react';

import { glowpNavigate } from '../../lib/navigate';
import { canSellSubscriptions } from '../../lib/platform';
export interface UpsellInfo {
  code: string;
  message: string;
  upgradePlan: 'BASIC' | 'PRO';
  used?: number;
  limit?: number;
}

interface UpsellModalProps {
  info: UpsellInfo | null;
  onClose: () => void;
}

const PLAN_DETAILS: Record<
  'BASIC' | 'PRO',
  { uiName: string; price: string; icon: React.ReactNode; perks: string[] }
> = {
  BASIC: {
    uiName: 'Premium',
    price: '4,99 €/mois',
    icon: <Sparkles className="w-5 h-5" />,
    perks: [
      'Fiches auto-remplies en 5 secondes',
      "Photos pro — fond blanc d'un clic",
      '5 modèles pour publier en série',
      'Boutique personnalisée',
      '3 boosts de visibilité par mois',
    ],
  },
  PRO: {
    uiName: 'Pro Business',
    price: '9,99 €/mois',
    icon: <Crown className="w-5 h-5" />,
    perks: [
      'Statut pro reconnu — acheteurs en confiance',
      'Factures + TVA prêtes pour ton comptable',
      'Comprends exactement ce qui se vend (et ce qui coince)',
      'Boosts et modèles illimités',
      'Achat protégé 14j affiché sur tes fiches',
    ],
  },
};

const UpsellModal: React.FC<UpsellModalProps> = ({ info, onClose }) => {
  if (!info) return null;

  const plan = PLAN_DETAILS[info.upgradePlan];

  // iOS natif : Apple App Store 3.1.1 interdit la vente d'abonnement hors
  // In-App Purchase. On affiche une modale neutre — la fonctionnalité est
  // nommée, sans prix, sans bouton d'achat, sans lien externe.
  if (!canSellSubscriptions()) {
    return (
      <div className="fixed inset-0 z-[80] flex items-center justify-center px-4">
        <div
          className="absolute inset-0 bg-black/55 backdrop-blur-sm"
          onClick={onClose}
          aria-hidden="true"
        />
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center">
          <div className="w-12 h-12 rounded-xl bg-brand-cream flex items-center justify-center mx-auto mb-3">
            <Lock className="w-5 h-5 text-brand-warm" />
          </div>
          <p className="text-base font-bold text-brand-dark mb-1">
            Une fonctionnalité Glowp {plan.uiName}
          </p>
          <p className="text-sm text-brand-warm leading-relaxed mb-5">
            Cette fonctionnalité fait partie de Glowp {plan.uiName}. Tu peux
            continuer à profiter de tout le reste de Glowp librement.
          </p>
          <button
            onClick={onClose}
            className="w-full py-3 rounded-xl bg-brand-500 text-white font-semibold text-sm hover:opacity-90 active:scale-[0.98] transition-all"
          >
            J'ai compris
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-black/55 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div
          className="px-5 py-4 flex items-center justify-between"
          style={{
            background:
              'linear-gradient(135deg, #C8A882 0%, #B89968 60%, #2d2a26 100%)',
          }}
        >
          <div className="flex items-center gap-2 text-white">
            <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
              {plan.icon}
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-white/80">
                Débloque {plan.uiName}
              </p>
              <p className="text-lg font-bold leading-tight">{plan.price}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-white/15 text-white"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 sm:p-6 space-y-4">
          <p className="text-sm text-brand-dark leading-relaxed">
            {info.message}
          </p>

          {info.used !== undefined && info.limit !== undefined && (
            <div className="rounded-xl bg-brand-cream px-3 py-2 text-xs text-brand-warm">
              Utilisation ce mois-ci :{' '}
              <strong className="text-brand-dark">{info.used}</strong> /{' '}
              {info.limit}
            </div>
          )}

          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-warm mb-2">
              Ce que tu débloques
            </p>
            <ul className="space-y-1.5">
              {plan.perks.map(perk => (
                <li
                  key={perk}
                  className="flex items-start gap-2 text-sm text-brand-dark"
                >
                  <span className="text-brand-500 mt-0.5">•</span>
                  <span>{perk}</span>
                </li>
              ))}
            </ul>
          </div>

          <button
            onClick={() => {
              onClose();
              // window.location pour fonctionner même hors Router context
              glowpNavigate('/pricing');
            }}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-brand-500 text-white font-semibold text-sm hover:opacity-90 active:scale-[0.98] transition-all"
          >
            Voir les plans <ArrowRight className="w-4 h-4" />
          </button>

          <button
            onClick={onClose}
            className="w-full text-xs text-brand-warm hover:text-brand-dark"
          >
            Plus tard
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpsellModal;
